import { DotIndicator } from "./dot-indicator.js";
import { polyfillScrollendEvent } from "./utils.js";

const template = document.createElement("template");

template.innerHTML = `
  <style>
    :host {
      display: grid;
      grid-template-areas: "previous . next";
      grid-template-columns: auto 1fr auto;
    }

    :host([hidden]) {
      display: none;
    }

    slot[name="previous"]::slotted(*),
    slot[name="next"]::slotted(*) {
      align-self: center;
      cursor: pointer;
      z-index: 1; /* Fixes safari layering issue */
    }

    slot[name="previous"]::slotted(*) {
      grid-area: previous;
    }

    slot[name="next"]::slotted(*) {
      grid-area: next;
    }

    slot:not([name])::slotted(*) {
      grid-area: 1 / 1 / -1 / -1;
      mask-image: var(--ws-scroller-mask-image);
    }

    slot[name="dot-indicator"]::slotted(*) {
      grid-column: 1 / -1;
    }

    .hidden {
      visibility: hidden;
    }
  </style>
  <slot></slot>
  <slot name="previous"></slot>
  <slot name="next"></slot>
  <slot name="dot-indicator"></slot>
`;

export class Scroller extends HTMLElement {
  #slots: Record<string, HTMLSlotElement> = {};
  #content: Element;
  #dots: DotIndicator;
  #item: Element;
  #items: NodeListOf<Element>;
  #currentItemNodeIndex = 0;
  #itemsObserver: IntersectionObserver;
  #itemWidth: number;
  #oneSlidePerMove: boolean;
  #gap: number;
  #intersectionRatios: number[] = [];

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot!.appendChild(template.content.cloneNode(true));
    this.#slots.previous = this.shadowRoot!.querySelector(
      `slot[name="previous"]`,
    ) as HTMLSlotElement;
    this.#slots.next = this.shadowRoot!.querySelector(
      `slot[name="next"]`,
    ) as HTMLSlotElement;
    this.#slots.default = this.shadowRoot!.querySelector(
      `slot:not([name])`,
    ) as HTMLSlotElement;
    this.#slots.dotIndicator = this.shadowRoot!.querySelector(
      `slot[name="dot-indicator"]`,
    )! as HTMLSlotElement;
    this.#dots =
      this.#slots.dotIndicator.assignedElements()[0]! as DotIndicator;
    this.#content = this.#slots.default.assignedElements()[0]!;
    this.#item = this.#content.querySelector(".scroller__item")!;
    this.#items = this.#content.querySelectorAll(".scroller__item")!;
    this.#itemWidth = 0;
    this.#oneSlidePerMove = false;
    const contentStyle = getComputedStyle(this.#content);
    if (contentStyle.display === "flex") {
      this.#gap = parseFloat(contentStyle.gap);
    } else {
      this.#gap = 0;
    }

    this.#itemsObserver = new IntersectionObserver(this.#handleIntersection, {
      root: this.#content,
      rootMargin: "0px",
      threshold: Array.from({ length: 11 }, (_, i) => i / 10),
    });
  }

  connectedCallback() {
    this.#oneSlidePerMove = this.hasAttribute("one-slide-per-move");
    /* get prev and next button */
    const previousSlot = this.#slots.previous!;
    const nextSlot = this.#slots.next!;

    /* set buttons initial on display none to prevent layout shift */
    previousSlot.style.display = "none";
    nextSlot.style.display = "none";

    /* delay function and displaying function 'til content is fully loaded (100ms is currently alright) */
    setTimeout(() => {
      this.#updateButtonsVisibility();
      previousSlot.style.display = "";
      nextSlot.style.display = "";
    }, 100);

    window.addEventListener("resize", this.#updateButtonsVisibility);
    previousSlot.addEventListener("click", this.#scrollPrevious);
    nextSlot.addEventListener("click", this.#scrollNext);
    if (!("onscrollend" in window)) polyfillScrollendEvent(this.#content); // Polyfills "scrollend" event in safari
    this.#content.addEventListener("scrollend", this.#updateButtonsVisibility);

    this.#intersectionRatios = new Array(this.#items.length).fill(0);

    for (const item of this.#items) {
      this.#itemsObserver.observe(item);
    }
  }

  #handleIntersection = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      if (entry.target instanceof HTMLElement) {
        const index = Array.prototype.indexOf.call(this.#items, entry.target);

        if (index !== -1) {
          this.#intersectionRatios[index] = entry.intersectionRatio;
        }
      }
    });

    const currentPosition = this.#calcPos();
    if (currentPosition !== this.#currentItemNodeIndex) {
      this.#currentItemNodeIndex = currentPosition;
      this.#updateDotIndicator();
    }
  };

  #calcPos(): number {
    const intersectSituation = this.#intersectionRatios
      .map((ratio) => (ratio < 0.95 ? "." : "X"))
      .join("");
    if (intersectSituation === "X..") return 1;
    if (intersectSituation === "XX.") return 1;
    if (intersectSituation === ".X.") return 2;
    if (intersectSituation === ".XX") return 3;
    if (intersectSituation === "..X") return 3;
    return 0;
  }

  #updateDotIndicator() {
    if (this.#currentItemNodeIndex == undefined) return;
    this.#dots.activeDot = this.#currentItemNodeIndex;
  }

  get itemWidth() {
    if (!this.#itemWidth) {
      this.#item = this.#content.querySelector(".scroller__item")!;
      this.#itemWidth = this.#item.getBoundingClientRect().width;
    }
    return this.#itemWidth;
  }

  #scrollPrevious = (e: Event) => {
    e.preventDefault();
    this.#content.scrollLeft -= this.#oneSlidePerMove
      ? this.itemWidth + this.#gap
      : this.#content.clientWidth * 0.5;
  };

  #scrollNext = (e: Event) => {
    e.preventDefault();
    this.#content.scrollLeft += this.#oneSlidePerMove
      ? this.itemWidth + this.#gap
      : this.#content.clientWidth * 0.5;
  };

  #updateButtonsVisibility = () => {
    const endScrollThreshold = 5; // on iPhone SE you need to scroll the last 5px to hide the arrow / gradient. imo it's a bad usability therefore this threshold solution
    const scrollLeft = this.#content.scrollLeft || 0;
    const clientWidth = this.#content.clientWidth || 0;
    const scrollWidth = this.#content.scrollWidth || 0;

    const isAtStart = scrollLeft === 0;
    const isAtEnd =
      scrollLeft + clientWidth >= scrollWidth - endScrollThreshold;

    this.#slots.previous!.classList.toggle("hidden", isAtStart);
    this.#slots.next!.classList.toggle("hidden", isAtEnd);

    // Manually set the mask-image value based on the visibility of the slots
    if (!isAtStart && !isAtEnd) {
      (this.#content as HTMLElement).style.setProperty(
        "--ws-scroller-mask-image",
        "var(--ws-scroller-mask-image-both)",
      );
    } else if (isAtStart && !isAtEnd) {
      (this.#content as HTMLElement).style.setProperty(
        "--ws-scroller-mask-image",
        "var(--ws-scroller-mask-image-next)",
      );
    } else if (!isAtStart && isAtEnd) {
      (this.#content as HTMLElement).style.setProperty(
        "--ws-scroller-mask-image",
        "var(--ws-scroller-mask-image-previous)",
      );
    } else {
      (this.#content as HTMLElement).style.setProperty(
        "--ws-scroller-mask-image",
        "none",
      );
    }
  };

  disconnectedCallback() {
    window.removeEventListener("resize", this.#updateButtonsVisibility);
    this.#slots.previous!.removeEventListener("click", this.#scrollPrevious);
    this.#slots.next!.removeEventListener("click", this.#scrollNext);
    this.#content.removeEventListener(
      "scrollend",
      this.#updateButtonsVisibility,
    );
  }
}

"customElements" in window &&
  customElements.get("ws-scroller") === undefined &&
  customElements.define("ws-scroller", Scroller);

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface HTMLElementTagNameMap {
    "ws-scroller": Scroller;
  }
}
